import {
  Frequency,
  Submission,
  Tag,
  Task,
  TaskKind,
  TaskTag,
} from "encamp-prisma/generated/browser";
import { DateTime } from "luxon";
import { RRule, Frequency as RRuleFrequency, Options as RRuleOptions, RRuleSet } from "rrule";

import { ExpandSeriesOptions, VirtualEventStatus } from "./types";

export const BEGINNING_OF_TIME = new Date("1970-01-01");
export const END_OF_TIME = new Date("9999-12-31");

/**
 * Expands a series of tasks into virtual events based on their recurrence rules.
 *
 * Creates virtual events according to each task's startDate, endDate, frequency, interval,
 * and excludedDates. The series will only be expanded out to 10 years from the current date
 * by default (unless a custom `before` date is provided in options).
 *
 * Field convention (ENG-14753): Virtual events spread all task fields including `title`
 * and `description`. For submission-linked tasks, `submission.title` and `submission.notes`
 * are the authoritative values — callers that display these fields should prefer the
 * submission values when `task.submission` is present. See `getDisplayTitle` and
 * `getDisplayNotes` in `encamp-shared/dist/tasks/displayFields`.
 *
 * @param tasks - Array of tasks to expand into virtual events
 * @param options - Expansion options including time window (after/before) and current time
 * @returns Array of virtual events with computed deadlines and statuses
 */
export function expandSeries<
  T extends Task & {
    tags?: Partial<Tag>[];
    taskTags?: Partial<TaskTag> & { tag: Tag }[];
    submission?: Submission | null;
  },
>(tasks: T[], options: ExpandSeriesOptions) {
  return tasks.flatMap((task) => {
    const rrule = getRRuleFromTask(task);
    const dates = rruleAll(rrule, options);
    return dates.map((date) => {
      const { id, ...rest } = task;

      return {
        ...rest,
        id: getVirtualEventId(id, date),
        taskId: id,
        deadline: date,
        status: getVirtualEventStatus({
          deadline: date,
          completedAt: task.completedAt,
          now: options.now,
        }),
        tags: task.tags ?? task.taskTags?.map((tt) => tt.tag) ?? [],
        submission: task.submission,
      };
    });
  });
}

/**
 * Get date of next deadline recurrence
 */
export function getNextDeadlineAfter(
  task: Pick<Task, "frequency" | "interval" | "startDate" | "endDate" | "excludedDates">
): Date {
  const rrule = new RRuleSet(true);
  rrule.rrule(new RRule(dbTaskToRRuleOptions(task)));
  task.excludedDates.forEach((d) => rrule.exdate(d));
  const now = DateTime.now().toJSDate();
  const fiveYearsFromNow = DateTime.now().plus({ years: 5 }).toJSDate();
  const dates = rruleAll(rrule, { after: now, before: fiveYearsFromNow });
  return dates[0];
}

function getRRuleFromTask(task: Task) {
  const rrule = new RRuleSet(true);
  rrule.rrule(new RRule(dbTaskToRRuleOptions(task)));
  task.excludedDates.forEach((d) => rrule.exdate(d));
  return rrule;
}

function rruleAll(rrule: RRuleSet, { after, before }: ExpandSeriesOptions) {
  const effectiveAfter = new Date(after ?? BEGINNING_OF_TIME);
  const effectiveBefore = new Date(before ?? DateTime.now().plus({ years: 10 }).toJSDate());
  return rrule.between(effectiveAfter, effectiveBefore, true);
}

export function dbTaskToRRuleOptions(
  task: Pick<Task, "frequency" | "interval" | "startDate" | "endDate">
): Partial<RRuleOptions> {
  const opts = mapFrequencyAndInterval(task);
  const startDate = task.startDate;

  if (
    startDate.getUTCDate() >= 28 &&
    (task.frequency === Frequency.MONTHLY || task.frequency === Frequency.QUARTERLY)
  ) {
    // Get the day of month from the start date
    const startDay = startDate.getUTCDate();
    // Create array starting at 28 up to startDay (inclusive)
    const bymonthday = [28, 29, 30, 31].slice(0, startDay - 27);

    return {
      ...opts,
      bymonthday,
      bysetpos: -1,
      dtstart: startDate,
      until: task.endDate,
    };
  }
  return {
    ...opts,
    dtstart: task.startDate,
    until: task.endDate,
  };
}

function mapFrequencyAndInterval(
  task: Pick<Task, "frequency" | "interval">
): Pick<RRuleOptions, "freq" | "interval"> | undefined {
  switch (task.frequency) {
    case Frequency.WEEKLY:
      return { freq: RRuleFrequency.WEEKLY, interval: task.interval };
    case Frequency.MONTHLY:
      return { freq: RRuleFrequency.MONTHLY, interval: task.interval };
    case Frequency.QUARTERLY:
      return { freq: RRuleFrequency.MONTHLY, interval: task.interval * 3 };
    case Frequency.YEARLY:
      return { freq: RRuleFrequency.YEARLY, interval: task.interval };
    case Frequency.ONCE:
    case null:
    default:
      return undefined;
  }
}

export function getVirtualEventId(taskId: string, deadline: Date) {
  return `${taskId}_${deadline.toISOString()}`;
}

export function parseVirtualEventId(eventId: string) {
  const [taskId, deadlineStr] = eventId.split("_");
  return {
    taskId,
    deadline: new Date(deadlineStr),
  };
}

export function prevDate(task: Task, date: Date) {
  const rrule = getRRuleFromTask(task);
  return rrule.before(date, false);
}

export function nextDate(task: Task, date: Date) {
  const rrule = getRRuleFromTask(task);
  return rrule.after(date, false);
}

function getVirtualEventStatus(task: {
  completedAt?: Date | null;
  deadline: Date;
  /**
   * Optional time for now
   * @default new Date()
   */
  now?: Date;
}): VirtualEventStatus {
  if (task.completedAt) {
    return VirtualEventStatus.Completed;
  }
  return new Date(task.deadline) < (task.now ?? new Date())
    ? VirtualEventStatus.Overdue
    : VirtualEventStatus.Active;
}

/**
 * Expands tasks into virtual events, handling ONCE tasks specially.
 *
 * ONCE tasks with materialized submissions use their submission.dueDate directly
 * instead of going through RRule (which would default to YEARLY frequency and
 * potentially generate incorrect dates based on task.startDate).
 *
 * This is necessary because:
 * 1. ONCE tasks may have task.startDate/endDate that don't match submission.dueDate
 *    (e.g., legacy data, seeder data with wide date ranges)
 * 2. RRule defaults to YEARLY for ONCE tasks, generating dates based on task.startDate
 * 3. The actual deadline for ONCE tasks is the submission.dueDate, not the RRule date
 *
 * @param tasksBySeriesId - Tasks grouped by seriesId
 * @param options - Expansion options including time window (after/before) and current time
 * @returns Array of virtual events with computed deadlines and statuses
 */
export function expandTasksWithOnceHandling<
  T extends Task & {
    tags?: Partial<Tag>[];
    taskTags?: Partial<TaskTag> & { tag: Tag }[];
    submission?: Submission | null;
  },
>(
  tasksBySeriesId: Record<string, T[]>,
  options: ExpandSeriesOptions
): Array<
  Omit<T, "id"> & {
    id: string;
    taskId: string;
    deadline: Date;
    status: VirtualEventStatus;
    tags: Partial<Tag>[];
    submission: Submission | null | undefined;
  }
> {
  const { after, before, now } = options;
  const effectiveAfter = new Date(after ?? BEGINNING_OF_TIME);
  const effectiveBefore = new Date(before ?? DateTime.now().plus({ years: 10 }).toJSDate());

  const onceTasksWithDueDate: T[] = [];
  const tasksForExpandSeries: Record<string, T[]> = {};

  // Separate instance/standalone tasks with submission.dueDate from templates
  for (const [seriesId, tasks] of Object.entries(tasksBySeriesId)) {
    for (const task of tasks) {
      if (task.kind !== TaskKind.TEMPLATE && task.submission?.dueDate) {
        // Instance/standalone task with submission - use dueDate directly
        onceTasksWithDueDate.push(task);
      } else {
        // Template task - use expandSeries for RRule expansion
        if (!tasksForExpandSeries[seriesId]) {
          tasksForExpandSeries[seriesId] = [];
        }
        tasksForExpandSeries[seriesId].push(task);
      }
    }
  }

  // Create virtual events for ONCE tasks using submission.dueDate
  const onceVirtualEvents = onceTasksWithDueDate
    .filter((task) => {
      const dueDate = new Date(task.submission!.dueDate!);
      return dueDate >= effectiveAfter && dueDate <= effectiveBefore;
    })
    .map((task) => {
      const dueDate = new Date(task.submission!.dueDate!);
      const { id, ...rest } = task;
      return {
        ...rest,
        id: getVirtualEventId(id, dueDate),
        taskId: id,
        deadline: dueDate,
        status: getVirtualEventStatus({
          deadline: dueDate,
          completedAt: task.completedAt,
          now,
        }),
        tags: task.tags ?? (task.taskTags?.map((tt) => tt.tag) as Partial<Tag>[]) ?? [],
        submission: task.submission,
      };
    });

  // Expand recurring series to get virtual events
  const recurringVirtualEvents = Object.values(tasksForExpandSeries).flatMap((tasks) =>
    expandSeries(tasks, options)
  );

  return [...onceVirtualEvents, ...recurringVirtualEvents];
}
